import type { IntentProps } from '../../../../core/slots/Intent/Intent.js';
import type { ZoomChangeHandler } from '../../../core/types/interactions.js';
import type { ToolbarPlacement } from '../../../core/types/toolbar.js';
import type {
  HistogramChartConfiguration,
  HistogramChartXAxisConfig,
} from '../../../histogram/types/histogram-props.js';

export type DerivedXYChartProps = {
  /** Resolved x-axis config (defaults applied). */
  xAxisConfig: HistogramChartXAxisConfig;
  /** Whether the XYChart x-axis should disable zoom. */
  disableZoom: boolean;
  /** Whether the XYChart x-axis should disable pan. */
  disablePan: boolean;
  /** Whether the XYChart toolbar should be visible. */
  toolbarEnabled: boolean;
  /** Toolbar placement (or undefined if defaults should apply). */
  toolbarPlacement: ToolbarPlacement | undefined;
  /** Resolved toolbar intents. */
  toolbarIntents: IntentProps[] | undefined;
  /**
   * Effective onZoomChange handler. Histogram exposes it both at root-prop
   * level and (after merging) inside `interactions`. Root prop wins.
   */
  onZoomChange: ZoomChangeHandler | undefined;
};

/**
 * Picks the `onZoomChange` handler from a resolved Histogram configuration.
 * `createHistogramConfiguration` stores it under `interactions.onZoomChange`
 * when it originates from props; root-prop values still win when provided
 * directly.
 */
export const pickOnZoomChange = (
  config: HistogramChartConfiguration,
  rootOnZoomChange: ZoomChangeHandler | undefined,
): ZoomChangeHandler | undefined =>
  rootOnZoomChange ??
  (config.interactions as { onZoomChange?: ZoomChangeHandler } | undefined)
    ?.onZoomChange;

/**
 * Derives the props the `HistogramToXY` adapter needs to forward to the
 * underlying `XYChart` (and its slots) from a resolved
 * `HistogramChartConfiguration`.
 */
export const deriveXYChartPropsFromConfig = (
  config: HistogramChartConfiguration,
  rootOnZoomChange: ZoomChangeHandler | undefined,
): DerivedXYChartProps => ({
  xAxisConfig: config.xAxis,
  disableZoom: config.interactions?.zoom?.enabled === false,
  disablePan: config.interactions?.pan?.enabled === false,
  toolbarEnabled: config.toolbar?.enabled !== false,
  toolbarPlacement: config.toolbar?.placement,
  toolbarIntents: config.toolbar?.intents,
  onZoomChange: pickOnZoomChange(config, rootOnZoomChange),
});
